'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { GenerationType } from '@/hooks/useGeneration';
import { Bubble, Driver, TaskSquare } from 'iconsax-reactjs';

interface OutputSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOutput: (
    types: GenerationType[],
    documentNames?: Record<GenerationType, string>,
  ) => void;
  title?: string;
}

export function OutputSelectionModal({
  isOpen,
  onClose,
  onSelectOutput,
  title = 'Select Output Types',
}: OutputSelectionModalProps) {
  const [selectedType, setSelectedType] = useState<GenerationType | null>(null);
  const [documentName, setDocumentName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const getPlaceholder = (type: GenerationType) => {
    return `${type} - V1 - Date`;
  };

  const outputTypes = [
    {
      type: 'Process Flow Doc' as GenerationType,
      icon: TaskSquare,
      description: 'Generate a process flow document',
    },
    {
      type: 'Discovery Doc' as GenerationType,
      icon: Bubble,
      description: 'Generate a discovery document',
    },
    {
      type: 'Backlog Doc' as GenerationType,
      icon: Driver,
      description: 'Generate a backlog document',
    },
  ];

  const handleTypeToggle = (type: GenerationType) => {
    if (selectedType === type) {
      // If clicking the same type, deselect it
      setSelectedType(null);
      setDocumentName('');
    } else {
      // If clicking a different type, select it and clear the previous value
      setSelectedType(type);
      setDocumentName('');
    }
  };

  // Focus input when a type is selected
  useEffect(() => {
    if (selectedType && inputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [selectedType]);

  const handleDocumentNameChange = (value: string) => {
    setDocumentName(value);
  };

  const isGenerateEnabled = () => {
    if (!selectedType) return false;

    // Check if the selected type has a non-empty document name
    return documentName.trim() !== '';
  };

  const handleGenerate = () => {
    if (isGenerateEnabled() && selectedType) {
      const documentNames: Record<GenerationType, string> = {
        'Process Flow Doc': '',
        'Discovery Doc': '',
        'Backlog Doc': '',
        [selectedType]: documentName,
      };
      onSelectOutput([selectedType], documentNames);
      setSelectedType(null);
      setDocumentName('');
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setDocumentName('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-400 pt-2">
          Choose an output and enter a file name in the input field to generate.
        </p>
        <div className="py-4 space-y-3">
          {outputTypes.map(({ type, icon: Icon, description }) => (
            <div key={type}>
              <div
                className={`w-full border rounded-md transition-colors ${
                  selectedType === type
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <button
                  onClick={() => handleTypeToggle(type)}
                  className="w-full flex items-center space-x-3 p-3 text-left hover:bg-inherit"
                >
                  <Icon className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{type}</div>
                    <div className="text-sm text-gray-500">{description}</div>
                  </div>
                </button>
                {selectedType === type && (
                  <div className="pl-11 pr-3 pb-3">
                    <Input
                      ref={inputRef}
                      placeholder={getPlaceholder(type)}
                      value={documentName}
                      onChange={(e) =>
                        handleDocumentNameChange(e.target.value)
                      }
                      className="w-full text-sm bg-white border-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex-row justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={handleClose} className="px-4 py-2">
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!isGenerateEnabled()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Generate {selectedType ? '(1)' : '(0)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
